import { FunctionComponent, memo, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../../../context';
import {
  CARDS_TO_BLOCK,
  EmbedLinkBlockData,
  getCssVar,
  IconLabel,
  LineClamp,
  useFreeLoginPopup,
} from '../../../../shared';

import classNames from 'classnames';
import classes from './EmbedLink.module.scss';

interface EmbedLinkProps extends EmbedLinkBlockData {
  hideUrl?: boolean;
  storyId?: number;
  cardId?: number;
}

export const EmbedLink: FunctionComponent<EmbedLinkProps> = memo(
  ({ link, hideUrl, storyId, cardId }) => {
    const { t } = useTranslation();

    const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

    const { isAnonymous } = useContext(UserContext).userProfile;

    const { sourceName, url, title, text, image, quoteSource, urlFormatted } = { ...link };

    const renderTeaser = useMemo(() => image?.url || quoteSource, [image?.url, quoteSource]);

    const isBlockedCard = useMemo(
      () =>
        Boolean(
          CARDS_TO_BLOCK.find(
            (cardToBlock) => cardToBlock.storyId === storyId && cardToBlock.cardId === cardId
          )
        ),
      [cardId, storyId]
    );

    const renderUrl = useMemo(
      () => !isBlockedCard && !hideUrl && url,
      [hideUrl, isBlockedCard, url]
    );

    const renderSocialDefaultImg = useMemo(
      () => !image?.url && quoteSource,
      [image?.url, quoteSource]
    );

    const onClickHandler = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isBlockedCard) {
          event.preventDefault();
          toast.info(t('common.blocked-card'));
          return;
        }

        if (isAnonymous && urlFormatted?.includes('www.riddle.com')) {
          event.preventDefault();
          freeLoginHandler();
          return;
        }
      },
      [freeLoginHandler, isAnonymous, isBlockedCard, t, urlFormatted]
    );

    if (!link) {
      return null;
    }

    return (
      <>
        <a
          draggable={false}
          href={urlFormatted ?? url}
          className={classNames(classes['embed-link'], { [classes['embed-link--no-url']]: !url })}
          target={'_blank'}
          rel={'noreferrer'}
          onClick={onClickHandler}
        >
          <div
            className={classNames(classes['embed-link__description'], {
              [classes['embed-link__description--with-teaser']]: renderTeaser,
            })}
          >
            {sourceName && (
              <div className={classes['embed-link__description-source']}>{sourceName}</div>
            )}
            {title && <div className={classes['embed-link__description-title']}>{title}</div>}
            {text && (
              <LineClamp
                maxLines={5}
                contentClassName={classes['embed-link__description-text']}
                readMoreClassName={classes['embed-link__description-text']}
                htmlStringContent={text}
                fallBackLineHeight={24} // used to fix editorJS issue
                readMore={'...'}
              />
            )}
            {renderUrl && (
              <div className={classes['embed-link__description-url']}>
                {quoteSource && (
                  <IconLabel
                    iconId={quoteSource.toLowerCase()}
                    iconSize={18}
                    color={getCssVar('--details-block-embed-icon-color')}
                    singleColor
                  />
                )}
                <span className={classes['embed-link__description-link']}>{url}</span>
              </div>
            )}
          </div>
          {renderTeaser && (
            <div
              className={classNames(classes['embed-link__image'], {
                [classes[`embed-link__image--${quoteSource}`]]: renderSocialDefaultImg,
              })}
              {...(image?.url && { style: { backgroundImage: `url(${unescape(image.url)})` } })}
            ></div>
          )}
        </a>

        {freeLoginModal}
      </>
    );
  }
);
